/* eslint-disable import/no-cycle */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import StyledTable, { TableBody, TableHead, TableCell, TableRow } from 'components/global/Table';
import { Select, MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import _ from 'lodash';
import TopBar from './partials/topBar';
import GlobalPanel from '../global/Panel';
import DataLoadingSpinner from '../global/DataLoadingSpinner';
import ConfirmationDialog from '../global/ConfirmationDialog';
import { fromInstant, getPreferenceTz } from 'utils/timezone';
import {
  fetchSharedWithAll,
  removeFileSharedToAll,
} from '../../actions/resources';

const propTypes = {
  fetchSharedWithAll: PropTypes.func.isRequired,
  removeFileSharedToAll: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

function FilesToAllClients({
  fetchSharedWithAll: fetchSharedWithAllAction,
  removeFileSharedToAll: removeFileSharedToAllAction,
  intl,
}) {
  const [sortBy, setSortBy] = useState('date-newest');
  const [snack, setSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  const fetchList = () => {
    setLoading(true);
    fetchSharedWithAllAction((resources, error) => {
      setLoading(false);
      if (!error) {
        setDocuments(_.map(resources, resource => resource));
      }
    });
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestClose = () => {
    setSnack(false);
  };

  const openSnack = message => {
    setSnack(true);
    setSnackMessage(message);
  };

  const showDeleteConfirm = fileId => {
    setDeletingFileId(fileId);
    setOpenDeleteConfirm(true);
  };

  const hideDeleteConfirm = () => setOpenDeleteConfirm(false);

  const handleChange = event => {
    setSortBy(event.target.value);
  };

  const handleRemoveFileSharedToAll = id => {
    removeFileSharedToAllAction(id, () => {
      openSnack(
        <FormattedMessage defaultMessage="Successfully deleted file" />,
      );
      fetchList();
    });
  };

  const renderRows = () => {
    if (_.isEmpty(documents)) {
      return (
        <TableRow>
          <TableCell>
            <FormattedMessage defaultMessage="No Files" />
          </TableCell>
        </TableRow>
      );
    }

    const filteredDocuments = [...documents];

    // sort
    filteredDocuments.sort((leftDoc, rightDoc) => {
      switch (sortBy) {
        case 'date-newest':
          return fromInstant(rightDoc.created_at).diff(
            fromInstant(leftDoc.created_at),
          );
        case 'date-oldest':
          return fromInstant(leftDoc.created_at).diff(
            fromInstant(rightDoc.created_at),
          );
        default:
      }
      return 0;
    });

    const { formatDate } = intl;

    return _.map(filteredDocuments, (document, index) => (
      <TableRow key={`ksc-${index + 1}`}>
        <TableCell>
          {formatDate(_.get(document, 'created_at', ''), {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: getPreferenceTz(),
          })}
        </TableCell>
        <TableCell>
          {_.get(document, 'file_name', '').replace(/__([a-zA-Z0-9])+\./, '.')}
        </TableCell>
        <TableCell>
          <div style={{ display: 'flex' }}>
            <Button
              href={`${_.get(document, 'resource.file.url', '')}`}
              className="flat-button-baby-blue process-action-button pab-small"
              download
              target="_blank"
            >{<FormattedMessage defaultMessage="View" />}</Button>
            &nbsp;
            <Button
              onClick={() =>
                showDeleteConfirm(_.get(document, 'id', null))
              }
              className="flat-button-red process-action-button pab-small"
            >{<FormattedMessage defaultMessage="Remove" />}</Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  const renderTable = () => (
    <StyledTable>
        <TableHead
         
        >
          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Date" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="File" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="Action" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows()}</TableBody>
      </StyledTable>
  );

  const { formatMessage } = intl;

  return (
    <div>
      <TopBar activeTab="files_to_all_clients" />
      <GlobalPanel>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: 30,
          }}
        >
          <Select value={sortBy} onChange={handleChange}>
            <MenuItem value="date-newest">
              {formatMessage({
                defaultMessage: 'Date - Newest First',
              })}
            </MenuItem>
            <MenuItem value="date-oldest">
              {formatMessage({
                defaultMessage: 'Date - Oldest First',
              })}
            </MenuItem>
          </Select>
        </div>
        {loading ? <DataLoadingSpinner /> : renderTable()}
      </GlobalPanel>
      {openDeleteConfirm && (
        <ConfirmationDialog
          open={openDeleteConfirm}
          message={formatMessage({
            defaultMessage: 'Are you sure you want to delete this file?',
          })}
          handleConfirm={() =>
            handleRemoveFileSharedToAll(deletingFileId)
          }
          handleClose={hideDeleteConfirm}
        />
      )}
      <Snackbar
        open={snack}
        message={snackMessage}
        autoHideDuration={4000}
        onClose={handleRequestClose}
      />
    </div>
  );
}

FilesToAllClients.propTypes = propTypes;

const mapStateToProps = state => ({
  resources: state.resources,
});

export default injectIntl(
  connect(mapStateToProps, {
    fetchSharedWithAll,
    removeFileSharedToAll,
  })(FilesToAllClients),
);
